import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNow,
  differenceInDays,
} from 'date-fns';
import {
  TIME_FORMAT,
  DATE_FORMAT_LONG,
  DATETIME_FORMAT_LONG,
  dateFnsLocale,
} from './dateFormat';

export const messageStamp = (time, dateFormat = TIME_FORMAT) => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, dateFormat, { locale: dateFnsLocale });
};

export const messageTimestamp = (time, dateFormat = DATE_FORMAT_LONG) => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  const messageDate = format(messageTime, dateFormat, {
    locale: dateFnsLocale,
  });
  if (!isSameYear(messageTime, now)) {
    return format(messageTime, DATETIME_FORMAT_LONG, { locale: dateFnsLocale });
  }
  return messageDate;
};

export const dynamicTime = time => {
  const unixTime = fromUnixTime(time);
  return formatDistanceToNow(unixTime, {
    addSuffix: true,
    locale: dateFnsLocale,
  });
};

export const dateFormat = (time, df = DATE_FORMAT_LONG) => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, df, { locale: dateFnsLocale });
};

// Converts a pt-BR `formatDistanceToNow` string (e.g. "há cerca de 2 horas") into
// the compact form used in conversation cards (e.g. "2h"). Pass withAgo=true to
// append " atrás".
export const shortTimestamp = (time, withAgo = false) => {
  const suffix = withAgo ? ' atrás' : '';
  const exactMappings = {
    'há menos de um minuto': 'agora',
    'há 1 minuto': `1m${suffix}`,
    'há cerca de 1 hora': `1h${suffix}`,
    'há 1 dia': `1d${suffix}`,
    'há cerca de 1 mês': `1mo${suffix}`,
    'há cerca de 1 ano': `1a${suffix}`,
  };
  if (exactMappings[time]) {
    return exactMappings[time];
  }
  const cleaned = time
    .replace(/^há /i, '')
    .replace(/^(cerca de |mais de |quase )/i, '');
  return cleaned
    .replace(/ minutos?$/, `m${suffix}`)
    .replace(/ horas?$/, `h${suffix}`)
    .replace(/ dias?$/, `d${suffix}`)
    .replace(/ meses$/, `mo${suffix}`)
    .replace(/ mês$/, `mo${suffix}`)
    .replace(/ anos?$/, `a${suffix}`);
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
